import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Rating,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import { useStyle } from "../../context/styleContext/styleContext";
import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
import { UserContext } from "../../context/userContext/userContext";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import LaptopMacRoundedIcon from "@mui/icons-material/LaptopMacRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { toast } from "react-hot-toast";
import LoginModal from "../LoginModal/LoginModal";
import shoppingCartIcon from "../../images/shopping_cart.svg";

export default function HomeFeaturedProductCard({ productData }) {
  const { user } = useContext(UserContext);
  const { addToCart } = useContext(ShoppingCartContext);
  const { style } = useStyle();
  const { getProductDisplayPricing } = usePromotions();

  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
  const [forwardToSubscribePage, setForwardToSubscribePage] = useState(false);
  const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
  const [macDownloadRequested, setMacDownloadRequested] = useState(false);

  const productPricing = useMemo(() => {
    if (!productData || Number(productData.price) <= 0) return null;
    return getProductDisplayPricing(productData);
  }, [productData, getProductDisplayPricing]);

  if (!productData) return null;

  const isFree = Number(productData.price) <= 0;
  const hasPromoDiscount = Boolean(productPricing?.hasDiscount);
  const finalPrice = Number(productPricing?.finalPrice ?? productData.price);
  const originalPrice = Number(productPricing?.originalPrice ?? productData.price);
  const savingsPercent =
    hasPromoDiscount && originalPrice > 0
      ? Math.round((Number(productPricing.discountAmount || 0) / originalPrice) * 100)
      : 0;

  const closeModal = (setModalFn) => () => setModalFn(false);
  const formatPrice = (price) => Number(price || 0).toFixed(2);

  const addProductToCart = (e) => {
    e.preventDefault();
    addToCart(productData.SKU);
  };

  const navToSubscribePage = async () => {
    if (user) {
      const userEmail = user.email;
      window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`;
    } else {
      setForwardToSubscribePage(true);
      setLoginRegisterModalOpen(true);
    }
  };

  const registerFreeThenDownload = async (platform) => {
    const token = localStorage.getItem("token");
    const productSKU = productData.SKU;
    const productVersion = productData.productVersion;

    if (!user) {
      if (platform === "windows") setWindowsDownloadRequested(true);
      if (platform === "mac") setMacDownloadRequested(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      const data = await axios.post("/api/register_free_product", {
        token,
        sku: productSKU,
        productVersion,
      });

      if (!data) {
        toast.error("Error: your request could not be completed at this time.");
        return;
      }
    } catch (error) {
      toast.error("Error: your request could not be completed at this time.");
      return;
    }

    window.location.href =
      platform === "windows"
        ? productData.windowsDownloadURI
        : productData.macDownloadURI;
  };

  const loginCallback = async (userEmail) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoginRegisterModalOpen(false);

    if (forwardToSubscribePage) {
      setForwardToSubscribePage(false);
      if (userEmail) {
        window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`;
      }
    }

    if (windowsDownloadRequested) {
      setWindowsDownloadRequested(false);

      try {
        const data = await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });

        if (!data) {
          toast.error("Error: your request could not be completed at this time.");
          return;
        }
      } catch (error) {
        toast.error("Error: your request could not be completed at this time.");
        return;
      }

      window.location.href = productData.windowsDownloadURI;
    }

    if (macDownloadRequested) {
      setMacDownloadRequested(false);

      try {
        const data = await axios.post("/api/register_free_product", {
          token,
          sku: productData.SKU,
          productVersion: productData.productVersion,
        });

        if (!data) {
          toast.error("Error: your request could not be completed at this time.");
          return;
        }
      } catch (error) {
        toast.error("Error: your request could not be completed at this time.");
        return;
      }

      window.location.href = productData.macDownloadURI;
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          minWidth: 0,
          borderRadius: "16px",
          backgroundColor: style.darkPanel,
          border: "1px solid rgba(255,255,255,0.08)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Box sx={{ width: { xs: "100%", md: "45%" }, minWidth: 0 }}>
              <Box
                component={RouterLink}
                to={`/products/${productData.productURI}`}
                sx={{
                  width: "100%",
                  minHeight: { xs: 240, md: 340 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  borderRadius: "14px",
                  backgroundColor: style.darkerPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                  p: 1.25,
                  boxSizing: "border-box",
                }}
              >
                <Box
                  component="img"
                  src={productData.images?.[2]?.URI || productData.images?.[0]?.URI}
                  alt={productData.images?.[2]?.altText || productData.images?.[0]?.altText}
                  sx={{
                    width: "95%",
                    maxHeight: 300,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack spacing={1}>
                <Typography
                  component={RouterLink}
                  to={`/products/${productData.productURI}`}
                  sx={{
                    color: style.color4,
                    textDecoration: "none",
                    fontWeight: 800,
                    fontSize: { xs: "1.6rem", md: "2.15rem" },
                    lineHeight: 1.05,
                    "&:hover": { color: style.color3 },
                  }}
                >
                  {productData.name}
                </Typography>

                <Box
                  component={RouterLink}
                  to={`/reviews/${productData.productURI}`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                    textDecoration: "none",
                    color: "inherit",
                    width: "fit-content",
                  }}
                >
                  <Rating value={Number(productData.starRating || 0)} precision={0.5} readOnly />
                  <Typography sx={{ fontWeight: 700, color: style.color4, fontSize: "1.05rem" }}>
                    {Number(productData.starRating || 0) > 0 ? productData.starRating : ""}
                  </Typography>
                  <Typography sx={{ color: style.color3 }}>
                    ({productData.ratingCount || 0} review{Number(productData.ratingCount) === 1 ? "" : "s"})
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: style.color3,
                    lineHeight: 1.72,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    maxWidth: 620,
                  }}
                >
                  {productData.cardSummary}
                </Typography>

                {!isFree ? (
                  <>
                    {hasPromoDiscount && (
                      <Chip
                        label={`SPRING SALE - ${savingsPercent}% off`}
                        size="small"
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.3,
                          borderRadius: "5px",
                          color: "#111",
                          backgroundColor: "#ffcc4d",
                          border: "1px solid rgba(255,255,255,0.35)",
                          fontWeight: 900,
                          fontSize: "0.78rem",
                          letterSpacing: "0.02em",
                        }}
                      />
                    )}

                    <Stack direction="row" spacing={1.25} alignItems="baseline" flexWrap="wrap" sx={{ pt: 0.4 }}>
                      <Typography
                        sx={{
                          color: style.color3,
                          fontSize: "1.2rem",
                          lineHeight: 1.1,
                          textDecoration: "line-through",
                        }}
                      >
                        ${formatPrice(hasPromoDiscount ? productData.price : productData.msrp)}
                      </Typography>

                      <Typography
                        sx={{
                          color: style.color4,
                          fontWeight: 800,
                          fontSize: { xs: "1.9rem", md: "2.4rem" },
                          lineHeight: 1.05,
                        }}
                      >
                        ${formatPrice(finalPrice)}
                      </Typography>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ pt: 1 }}
                    >
                      <Button
                        onClick={addProductToCart}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 900,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          minWidth: { sm: 150 },
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        <img
                          src={shoppingCartIcon}
                          alt="Shopping cart icon"
                          style={{ maxWidth: "18px", marginRight: "6px" }}
                        />
                        Add to Cart
                      </Button>

                      <Button
                        href="/subscribe"
                        startIcon={<BoltRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 900,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          minWidth: { sm: 160 },
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" },
                        }}
                      >
                        Try for Free
                      </Button>

                      <Button
                        component={RouterLink}
                        to={`/products/${productData.productURI}`}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 900,
                          color: style.color4,
                          backgroundColor: "transparent",
                          border: "1px solid rgba(255,255,255,0.10)",
                          minWidth: { sm: 150 },
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                        }}
                      >
                        <ArrowForwardRoundedIcon sx={{ fontSize: "1.3rem", mr: 0.5 }} />
                        Learn More
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        color: style.color4,
                        fontSize: "1.55rem",
                        lineHeight: 1.15,
                        pt: 0.8,
                      }}
                    >
                      <strong style={{ color: "rgb(0,200,50)" }}>FREE</strong>
                    </Typography>

                    <Typography
                      sx={{
                        color: style.color3,
                        fontWeight: 700,
                        fontSize: "0.98rem",
                      }}
                    >
                      Download VST3 Plugin:
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ pt: 0.75 }}
                    >
                      <Button
                        onClick={() => registerFreeThenDownload("windows")}
                        startIcon={<DesktopWindowsRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          minWidth: { sm: 150 },
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        Windows
                      </Button>

                      <Button
                        onClick={() => registerFreeThenDownload("mac")}
                        startIcon={<LaptopMacRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          minWidth: { sm: 150 },
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" },
                        }}
                      >
                        Mac
                      </Button>

                      <Button
                        component={RouterLink}
                        to={`/products/${productData.productURI}`}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "transparent",
                          border: "1px solid rgba(255,255,255,0.10)",
                          minWidth: { sm: 150 },
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                        }}
                      >
                        <ArrowForwardRoundedIcon sx={{ fontSize: "1.3rem", mr: 0.5 }} />
                        Learn More
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <LoginModal
        isOpen={isLoginRegisterModalOpen}
        close={closeModal(setLoginRegisterModalOpen)}
        loginComplete={loginCallback}
      />
    </>
  );
}

// import React, { useContext, useState } from "react";
// import axios from "axios";
// import { Link as RouterLink } from "react-router-dom";
// import {
//   Box,
//   Button,
//   Typography,
//   Rating,
//   Stack,
//   Paper,
// } from "@mui/material";
// import { useStyle } from "../../context/styleContext/styleContext";
// import { ShoppingCartContext } from "../../context/shoppingCartContext/shoppingCartContext";
// import { UserContext } from "../../context/userContext/userContext";
// import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
// import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
// import LaptopMacRoundedIcon from "@mui/icons-material/LaptopMacRounded";
// import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
// import { toast } from "react-hot-toast";
// import LoginModal from "../LoginModal/LoginModal";
// import shoppingCartIcon from "../../images/shopping_cart.svg";

// export default function HomeFeaturedProductCard({ productData }) {
//   const { user } = useContext(UserContext);
//   const { addToCart } = useContext(ShoppingCartContext);
//   const { style } = useStyle();

//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [forwardToSubscribePage, setForwardToSubscribePage] = useState(false);
//   const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//   const [macDownloadRequested, setMacDownloadRequested] = useState(false);

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   if (!productData) return null;

//   const isFree = Number(productData.price) <= 0;

//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//   };

//   const navToSubscribePage = async () => {
//     if (user) {
//       const userEmail = user.email;
//       window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`;
//     } else {
//       setForwardToSubscribePage(true);
//       setLoginRegisterModalOpen(true);
//     }
//   };

//   const registerFreeThenDownload = async (platform) => {
//     const token = localStorage.getItem("token");
//     const productSKU = productData.SKU;
//     const productVersion = productData.productVersion;

//     if (!user) {
//       if (platform === "windows") setWindowsDownloadRequested(true);
//       if (platform === "mac") setMacDownloadRequested(true);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       const data = await axios.post("/api/register_free_product", {
//         token: token,
//         sku: productSKU,
//         productVersion,
//       });

//       if (!data) {
//         toast.error("Error: your request could not be completed at this time.");
//         return;
//       }
//     } catch (error) {
//       toast.error("Error: your request could not be completed at this time.");
//       return;
//     }

//     window.location.href =
//       platform === "windows"
//         ? productData.windowsDownloadURI
//         : productData.macDownloadURI;
//   };

//   const loginCallback = async (userEmail) => {
//     const token = localStorage.getItem("token");
//     if (!token) return;

//     setLoginRegisterModalOpen(false);

//     if (forwardToSubscribePage) {
//       setForwardToSubscribePage(false);
//       if (userEmail) {
//         window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`;
//       }
//     }

//     if (windowsDownloadRequested) {
//       setWindowsDownloadRequested(false);

//       try {
//         const data = await axios.post("/api/register_free_product", {
//           token: token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });

//         if (!data) {
//           toast.error("Error: your request could not be completed at this time.");
//           return;
//         }
//       } catch (error) {
//         toast.error("Error: your request could not be completed at this time.");
//         return;
//       }

//       window.location.href = productData.windowsDownloadURI;
//     }

//     if (macDownloadRequested) {
//       setMacDownloadRequested(false);

//       try {
//         const data = await axios.post("/api/register_free_product", {
//           token: token,
//           sku: productData.SKU,
//           productVersion: productData.productVersion,
//         });

//         if (!data) {
//           toast.error("Error: your request could not be completed at this time.");
//           return;
//         }
//       } catch (error) {
//         toast.error("Error: your request could not be completed at this time.");
//         return;
//       }

//       window.location.href = productData.macDownloadURI;
//     }
//   };

//   const formatPrice = (price) => Number(price).toFixed(2);

//   return (
//     <>
//       <Paper
//         elevation={0}
//         sx={{
//           width: "100%",
//           minWidth: 0,
//           borderRadius: "16px",
//           backgroundColor: style.darkPanel,
//           border: "1px solid rgba(255,255,255,0.08)",
//           overflow: "hidden",
//         }}
//       >
//         <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
//           <Stack
//             direction={{ xs: "column", md: "row" }}
//             spacing={{ xs: 1.5, md: 2 }}
//             alignItems={{ xs: "stretch", md: "center" }}
//           >
//             <Box
//               sx={{
//                 width: { xs: "100%", md: "45%" },
//                 minWidth: 0,
//               }}
//             >
//               <Box
//                 component={RouterLink}
//                 to={`/products/${productData.productURI}`}
//                 sx={{
//                   width: "100%",
//                   minHeight: { xs: 240, md: 340 },
//                   display: "flex",
//                   alignItems: "center",
//                   justifyContent: "center",
//                   textDecoration: "none",
//                   borderRadius: "14px",
//                   backgroundColor: style.darkerPanel,
//                   border: "1px solid rgba(255,255,255,0.06)",
//                   p: 1.25,
//                   boxSizing: "border-box",
//                 }}
//               >
//                 <Box
//                   component="img"
//                   src={productData.images?.[2]?.URI || productData.images?.[0]?.URI}
//                   alt={productData.images?.[2]?.altText || productData.images?.[0]?.altText}
//                   sx={{
//                     width: "95%",
//                     maxHeight: 300,
//                     objectFit: "contain",
//                     display: "block",
//                   }}
//                 />
//               </Box>
//             </Box>

//             <Box sx={{ flex: 1, minWidth: 0 }}>
//               <Stack spacing={1}>
//                 <Typography
//                   component={RouterLink}
//                   to={`/products/${productData.productURI}`}
//                   sx={{
//                     color: style.color4,
//                     textDecoration: "none",
//                     fontWeight: 800,
//                     fontSize: { xs: "1.6rem", md: "2.15rem" },
//                     lineHeight: 1.05,
//                     "&:hover": {
//                       color: style.color3,
//                     },
//                   }}
//                 >
//                   {productData.name}
//                 </Typography>

//                 <Box
//                   component={RouterLink}
//                   to={`/reviews/${productData.productURI}`}
//                   sx={{
//                     display: "inline-flex",
//                     alignItems: "center",
//                     gap: 0.75,
//                     flexWrap: "wrap",
//                     textDecoration: "none",
//                     color: "inherit",
//                     width: "fit-content",
//                   }}
//                 >
//                   <Rating
//                     value={Number(productData.starRating || 0)}
//                     precision={0.5}
//                     readOnly
//                   />
//                   <Typography sx={{ fontWeight: 700, color: style.color4, fontSize: "1.05rem" }}>
//                     {Number(productData.starRating || 0) > 0 ? productData.starRating : ""}
//                   </Typography>
//                   <Typography sx={{ color: style.color3 }}>
//                     ({productData.ratingCount || 0} review{Number(productData.ratingCount) === 1 ? "" : "s"})
//                   </Typography>
//                 </Box>

//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     lineHeight: 1.72,
//                     fontSize: { xs: "0.98rem", md: "1.02rem" },
//                     maxWidth: 620,
//                   }}
//                 >
//                   {productData.cardSummary}
//                 </Typography>

//                 {!isFree ? (
//                   <>
//                     <Typography
//                       sx={{
//                         color: style.color3,
//                         fontSize: "1.2rem",
//                         lineHeight: 1.1,
//                         pt: 0.6,
//                       }}
//                     >
//                       <span style={{ textDecoration: "line-through" }}>
//                         ${formatPrice(productData.msrp)}
//                       </span>
//                     </Typography>

//                     <Typography
//                       sx={{
//                         color: style.color4,
//                         fontWeight: 800,
//                         fontSize: { xs: "1.9rem", md: "2.4rem" },
//                         lineHeight: 1.05,
//                       }}
//                     >
//                       ${formatPrice(productData.price)}
//                     </Typography>

//                     <Stack
//                       direction={{ xs: "column", sm: "row" }}
//                       spacing={1}
//                       flexWrap="wrap"
//                       useFlexGap
//                       sx={{ pt: 1 }}
//                     >
//                       <Button
//                         onClick={addProductToCart}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 900,
//                           color: style.color4,
//                           backgroundColor: style.accentGreen,
//                           minWidth: { sm: 150 },
//                           "&:hover": {
//                             backgroundColor: style.accentGreen,
//                             filter: "brightness(1.05)",
//                           },
//                         }}
//                       >
//                         <img
//                           src={shoppingCartIcon}
//                           alt="Shopping cart icon"
//                           style={{ maxWidth: "18px", marginRight: "6px" }}
//                         />
//                         Add to Cart
//                       </Button>

//                       <Button
//                         href="/subscribe"
//                         startIcon={<BoltRoundedIcon />}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 900,
//                           color: style.color4,
//                           backgroundColor: "rgba(255,255,255,0.05)",
//                           border: "1px solid rgba(255,255,255,0.10)",
//                           minWidth: { sm: 160 },
//                           "&:hover": {
//                             backgroundColor: "rgba(255,255,255,0.10)",
//                           },
//                         }}
//                       >
//                         Try for Free
//                       </Button>

//                       <Button
//                         component={RouterLink}
//                         to={`/products/${productData.productURI}`}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 900,
//                           color: style.color4,
//                           backgroundColor: "transparent",
//                           border: "1px solid rgba(255,255,255,0.10)",
//                           minWidth: { sm: 150 },
//                           "&:hover": {
//                             backgroundColor: "rgba(255,255,255,0.04)",
//                           },
//                         }}
//                       >
//                         <ArrowForwardRoundedIcon sx={{ fontSize: "1.3rem", mr: 0.5 }} />
//                         Learn More
//                       </Button>
//                     </Stack>
//                   </>
//                 ) : (
//                   <>
//                     <Typography
//                       sx={{
//                         color: style.color4,
//                         fontSize: "1.55rem",
//                         lineHeight: 1.15,
//                         pt: 0.8,
//                       }}
//                     >
//                       <strong style={{ color: "rgb(0,200,50)" }}>FREE</strong>
//                     </Typography>

//                     <Typography
//                       sx={{
//                         color: style.color3,
//                         fontWeight: 700,
//                         fontSize: "0.98rem",
//                       }}
//                     >
//                       Download VST3 Plugin:
//                     </Typography>

//                     <Stack
//                       direction={{ xs: "column", sm: "row" }}
//                       spacing={1}
//                       flexWrap="wrap"
//                       useFlexGap
//                       sx={{ pt: 0.75 }}
//                     >
//                       <Button
//                         onClick={() => registerFreeThenDownload("windows")}
//                         startIcon={<DesktopWindowsRoundedIcon />}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 700,
//                           color: style.color4,
//                           backgroundColor: style.accentGreen,
//                           minWidth: { sm: 150 },
//                           "&:hover": {
//                             backgroundColor: style.accentGreen,
//                             filter: "brightness(1.05)",
//                           },
//                         }}
//                       >
//                         Windows
//                       </Button>

//                       <Button
//                         onClick={() => registerFreeThenDownload("mac")}
//                         startIcon={<LaptopMacRoundedIcon />}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 700,
//                           color: style.color4,
//                           backgroundColor: "rgba(255,255,255,0.05)",
//                           border: "1px solid rgba(255,255,255,0.10)",
//                           minWidth: { sm: 150 },
//                           "&:hover": {
//                             backgroundColor: "rgba(255,255,255,0.10)",
//                           },
//                         }}
//                       >
//                         Mac
//                       </Button>

//                       <Button
//                         component={RouterLink}
//                         to={`/products/${productData.productURI}`}
//                         sx={{
//                           borderRadius: "5px",
//                           textTransform: "none",
//                           fontWeight: 700,
//                           color: style.color4,
//                           backgroundColor: "transparent",
//                           border: "1px solid rgba(255,255,255,0.10)",
//                           minWidth: { sm: 150 },
//                           "&:hover": {
//                             backgroundColor: "rgba(255,255,255,0.04)",
//                           },
//                         }}
//                       >
//                         <ArrowForwardRoundedIcon sx={{ fontSize: "1.3rem", mr: 0.5 }} />
//                         Learn More
//                       </Button>
//                     </Stack>
//                   </>
//                 )}
//               </Stack>
//             </Box>
//           </Stack>
//         </Box>
//       </Paper>

//       <LoginModal
//         isOpen={isLoginRegisterModalOpen}
//         close={closeModal(setLoginRegisterModalOpen)}
//         loginComplete={loginCallback}
//       />
//     </>
//   );
// }

// import {React, useContext, useState, useEffect} from 'react';
// import { useTheme } from '@mui/material/styles';
// import{UserContext} from '../../context/userContext/userContext'
// import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
// import {Divider, Button, Box, Typography} from '@mui/material/';
// import axios from 'axios';
// import { useStyle } from '../../context/styleContext/styleContext';
// import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
// import Rating from '@mui/material/Rating';
// import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
// import shoppingCartIcon from '../../images/shopping_cart.svg'
// import LoginModal from '../LoginModal/LoginModal';
// import {toast} from 'react-hot-toast'

// export default function HomeFeaturedProductCard({productData}) {

//     const [data, setData] = useState();
//     const { user } = useContext(UserContext);
//     const { cartItems, cartSubtotal, addToCart } = useContext(ShoppingCartContext);
//     const { style } = useStyle();
//     const [width, setWidth] = useState(window.setWidth)
//     const closeModal = setModalFn => () => setModalFn(false);
//     const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//     const [forwardToSubscribePage, setForwardToSubscribePage] = useState(false);
//     const [windowsDownloadRequested, setWindowsDownloadRequested] = useState(false);
//     const [macDownloadRequested, setMacDownloadRequested] = useState(false);
//     const isMobile = width <= 768;

//     useEffect(() => {
//         if(productData) {
//             setData(productData)
//         }
        
//     }, [productData]);

//     useEffect(() => {
//         function handleResize() {      
//           setWidth(window.innerWidth)
//       }
      
//         window.addEventListener('resize', handleResize)
//       })
// useEffect(() => {
//     setWidth(window.innerWidth)
// }, []);
//   //const [productData, setProductData] = useState();

//   const downloadWindowsVST = async () => {
    
//     console.log("Windows Download....")
//     const token = localStorage.getItem('token')
//     const productSKU = productData.SKU;
//     const productVersion = productData.productVersion;
//     setWindowsDownloadRequested(true);
      
//       if(user)
//       {
//         console.log("windows click..")

//         try{


//         const data = await axios.post('/api/register_free_product', { token:token, sku:productSKU, productVersion:productVersion });

//         if(!data)
//         {
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }
        
//         }catch(error){
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }

//         var downloadLocation = productData.windowsDownloadURI;
//         window.location.href = downloadLocation;  

//       }
//       else
//       {
//         setLoginRegisterModalOpen(true)
//       }

//   }



//   const navToSubscribePage = async () => {
//     console.log("nav to subscribe")
//   if(user)
//   {
//     const userEmail = user.email;
//     window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`

//   }
//   else
//   {
//     setForwardToSubscribePage(true)
//     setLoginRegisterModalOpen(true)
//   }

//   }

//   const downloadMacVST = async () => {
//     console.log("Mac Download....")
//     const token = localStorage.getItem('token')
//     const productSKU = productData.SKU;
//     const productVersion = productData.productVersion;
//     setMacDownloadRequested(true);
      
//       if(user)
//       {
//         console.log("mac click..")

//         try{


//         const data = await axios.post('/api/register_free_product', { token:token, sku:productSKU, productVersion:productVersion });

//         if(!data)
//         {
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }
        
//         }catch(error){
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }

//         var downloadLocation = productData.macDownloadURI;
//         window.location.href = downloadLocation; 

//       }
//       else
//       {
//         setLoginRegisterModalOpen(true)
//       }
//   }



//   const downloadMac = async (e) => {


//     e.preventDefault()
          
//           const dataToSend = {
//             sku: productData.SKU,
//             productName: productData.name,
//             productVersion: "Mac " + productData.productVersion,
//             downloadURI: "Product Card",
//           }
  
//           console.log("attemting download")
  
//           try {
//             const { data } = await axios.post('/log-download-attempt', dataToSend);
  
//         } catch (error) {
  
//         }
  
//         var downloadLocation = productData.macDownloadURI;
//         window.location.href = downloadLocation; 
//   }


//   const addProductToCart = (e) => {
//     e.preventDefault();
//     addToCart(productData.SKU);
//   };

//   const loginCallback = async (userEmail) => {

//     console.log("Login process complete!")
//     const token = localStorage.getItem('token')
//     if(!token)
//     {
//       return
//     }
//     setLoginRegisterModalOpen(false);
//     console.log("User email? ")
//     console.log(userEmail);
    
    
//     if(forwardToSubscribePage)
//     {
//       setForwardToSubscribePage(false)
//       if(userEmail){
//         window.location.href = `https://buy.stripe.com/6oE3gfaAxdanaticMM?prefilled_email=${encodeURIComponent(userEmail)}`
//       }
//     }
//     if(windowsDownloadRequested)
//     {
//       setWindowsDownloadRequested(false);
//       // var downloadLocation = productData.windowsDownloadURI;
//       // window.location.href = downloadLocation;
//       try{
//         const data = await axios.post('/api/register_free_product', { token:token, sku:productData.SKU, productVersion:productData.productVersion });

//         if(!data)
//         {
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }
        
//         }catch(error){
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }

//         var downloadLocation = productData.windowsDownloadURI;
//         window.location.href = downloadLocation; 

//     }
//     if(macDownloadRequested)
//     {
//       setMacDownloadRequested(false);
//       // var downloadLocation = productData.macDownloadURI;
//       // window.location.href = downloadLocation;
//       try{
//         const data = await axios.post('/api/register_free_product', { token:token, sku:productData.SKU, productVersion:productData.productVersion });

//         if(!data)
//         {
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }
        
//         }catch(error){
//           toast.error("Error: your request could not be completed at this time.")
//           return
//         }

//         var downloadLocation = productData.macDownloadURI;
//         window.location.href = downloadLocation; 
//     }

//   };


  
//   const downloadWindows = async (e) =>
//       {
//           //console.log("logging in...")
  
//           e.preventDefault()
          
//           const dataToSend = {
//             sku: productData.SKU,
//             productName: productData.name,
//             productVersion: "Windows " + productData.productVersion,
//             downloadURI: "Product Card",
//           }
  
//           console.log("attemting download")
  
//           try {
//             const { data } = await axios.post('/log-download-attempt', dataToSend);
  
//         } catch (error) {
  
//         }
  
  
//       window.location.href = productData.windowsDownloadURI
  
  
//       }
  



//   return (<>
//   {data && isMobile && (
//    <Box sx={{width:'100%', height:'100%', display:'flex', flexDirection:'column', p:0}}>
//     <Box sx={{ width:'90%', borderRadius:'4px', p:1,  textAlign: "center", flex:1, minWidth:'75px', backgroundColor:'rgba(0,0,0,0.3)', margin:'auto', mt:1}}>
               
//                 <a href={'/products/' + productData.productURI}>
//                 <img
//                   src={productData.images[2]?.URI}
//                   alt={productData.images[2]?.altText}
//                   style={{
//                     width:'95%',
//                     borderRadius:'8px', 
//                     objectFit: "contain",
//                     margin:'auto',
//                     backgroundColor:'rgba(0,0,0,.3)',
//                     mt:2,
//                     mb:2
//                   }}
//                 />
//                 </a>


                
//               </Box>
//               <Box sx={{flex:1}}>
//               <Typography variant='h3' sx={{mt:1, fontSize:'1.2rem', textAlign:'center', width:'100%', fontWeight:'bold'}}> {productData.name} - VST3 Plugin</Typography>
//               <Divider sx={{margin:'auto', mt:1, mb:1, backgroundColor:style.color2, width:'95%'}} />
//               <div style={{display:'flex', flexDirection:'column', width:'100%'}}>

//                   <Box sx={{display:'flex', flexDirection:'row', textAlign:'left', width:'100%', ml:1 }}>
//                   <a href={'/reviews/' + productData.productURI}>
//                 <Rating sx={{fontSize:'1.5rem'}}value={productData.starRating} precision={0.5} readOnly />
//                     </a>
//                     <a href={'/reviews/' + productData.productURI}>
//                     <Typography sx={{ ml: 1, fontSize:'1.2rem'}}>{productData.ratingCount > 0 && (productData.starRating)}</Typography>
//                     </a>
//                   </Box>
//                   <a href={'/reviews/' + productData.productURI}>
//                     <Typography sx={{ ml: 1, fontSize:'1rem', textAlign:'left' }}> ({productData.ratingCount} {productData.ratingCount === 1 && (<>review</> )} {productData.ratingCount != 1 && (<>reviews</> )})</Typography>
//                     </a>
//                   <Divider sx={{margin:'auto', mt:.5, mb:.5, mr:.5, backgroundColor:style.color2, width:'95%'}} />
//                   <Typography sx={{ ml: 0.5, fontSize:'.8rem', fontWeight:'bold', textAlign:'left' }}> {productData.cardSummary}</Typography>
//                   <Typography sx={{ ml: 0.5, mb:1, mt:0.5, fontSize:'.8rem', textAlign:'left' }}> <strong>VST3 Plugin</strong> - available for both Windows and Mac</Typography>
//                   {productData.price > 0 ? (
//                     <>
//                     <Typography
//                         variant="body2"
//                         sx={{ color: style.color3, ml:3,textDecoration: "line-through", fontSize: "1.5rem", textAlign:'left' }}
//                       >
//                         ${productData.msrp.toFixed(2)}
//                       </Typography>
//                       <Typography variant="h6" sx={{ml:3, mb:2, color: style.color4, fontSize:'2.0rem', textAlign:'left' }}>
//                         <strong>${(productData.price).toFixed(2)}</strong>
//                       </Typography>
                      
//                       <Button
//                         onClick={addProductToCart}
//                         sx={{
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                           border: `1px solid ${style.color2}`,
//                           width:'95%',
//                           margin:'auto',
//                           filter:'brightness(1.05)',
//                           boxShadow:10,
//                           fontWeight:'bold',
//                           fontSize:'.8rem',
//                           "&:hover": { filter:'brightness(1.2)'},
//                         }}
//                       >
//                         <img src={shoppingCartIcon} alt='Shopping cart icon' style={{ maxWidth:'20px', marginRight:'5px'}} />
//                         Add to Cart
//                       </Button>
//                       <Button
//                         href={'/products/' + productData.productURI}
//                         sx={{
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                           border: `1px solid ${style.color2}`,
//                           width:'95%',
//                           margin:'auto',
//                           filter:'brightness(1.05)',
//                           boxShadow:10,
//                           mt:2,
//                           mb:2,
//                           fontWeight:'bold',
//                           fontSize:'.8rem',
//                           "&:hover": { filter:'brightness(1.2)'},
//                         }}
//                       >
//                         <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize:'1.5rem', marginRight:'5px'}}/>
//                         Learn More
//                       </Button>
//                     </>
//                   ) : (
//                     <>
//                         {/* Free Product */}
//                     <Box sx={{display:'flex', flexDirection:'row'}}>
//                     <Typography sx={{ml:0.5, color:'rgb(0,255,0)', fontSize:'1.5rem', fontWeight:'bold', textAlign:'left' }}> FREE </Typography>
//                     </Box>
//                     <Typography sx={{ml:.5, color:style.color4, fontSize:'1rem', textAlign:'left', fontWeight:'bold'}}>Download VST3 Plugin:</Typography>
//                     <div style={{ textAlign:'left', display:'flex', flexDirection:'row' }}>
//                     <Button onClick={downloadWindowsVST} sx={{ m:0.25, mt:0, minHeight:'60px', boxShadow:5, backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
                    
//                     <div style={{flex:1, minWidth:'60px' }}>
//                         <div style={{flex:1, padding:'10px', paddingBottom:'5px', backgroundColor:'black'}}>
//                             <img src='/windows_logo.png' style={{width:'100%', margin:'auto'}} />
//                         </div>
//                         <div style={{textAlign:'center', color:style.color4}}>
//                         <strong>Windows</strong>
//                             </div>
//                             </div>
//                     </Button>
//                     <Button onClick={downloadMacVST} sx={{ m:0.25, mt:0, boxShadow:5, minHeight:'60px', backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
                    
//                     <div style={{flex:1, minWidth:'60px'}}>
//                         <div style={{flex:1, padding:'10px', paddingBottom:'5px', backgroundColor:style.color4}}>
//                             <img src='/apple_logo.svg' style={{width:'95%', margin:'auto'}} />
//                         </div>
//                         <div style={{textAlign:'center', color:style.color4}}>
//                         <strong>Mac</strong>
//                             </div>
//                             </div>
//                     </Button>
//                     </div>
                

//                     </>
//                   )}

//               </div> 
              
//               </Box>
//   </Box>
//   )}
  
//   {data && !isMobile && (
//     <Box sx={{width:'100%', height:'100%', display:'flex', flexDirection:'column', p:0}}>
//     <Box sx={{ width:'90%', borderRadius:'4px', p:1,  textAlign: "center", flex:1, minWidth:'75px', backgroundColor:'rgba(0,0,0,0.3)', margin:'auto', mt:1}}>
               
//                 <a href={'/products/' + productData.productURI}>
//                 <img
//                   src={productData.images[2]?.URI}
//                   alt={productData.images[2]?.altText}
//                   style={{
//                     width:'95%',
//                     borderRadius:'8px', 
//                     objectFit: "contain",
//                     margin:'auto',
//                     backgroundColor:'rgba(0,0,0,.3)',
//                     mt:2,
//                     mb:2
//                   }}
//                 />
//                 </a>


                
//               </Box>
//               <Box sx={{flex:1}}>
//               <Typography variant='h3' sx={{mt:1, fontSize:'1.4rem', textAlign:'center', width:'100%', fontWeight:'bold'}}> {productData.name} - VST3 Plugin</Typography>
//               <Divider sx={{margin:'auto', mt:1, mb:1, backgroundColor:style.color2, width:'95%'}} />
//               <div style={{display:'flex', flexDirection:'column', width:'100%'}}>

//                   <Box sx={{display:'flex', flexDirection:'row', textAlign:'left', width:'100%', ml:1 }}>
//                   <a href={'/reviews/' + productData.productURI}>
//                 <Rating sx={{fontSize:'1.5rem'}}value={productData.starRating} precision={0.5} readOnly />
//                     </a>
//                     <a href={'/reviews/' + productData.productURI}>
//                     <Typography sx={{ ml: 1, fontSize:'1.2rem'}}>{productData.ratingCount > 0 && (productData.starRating)}</Typography>
//                     </a>
//                   </Box>
//                   <a href={'/reviews/' + productData.productURI}>
//                     <Typography sx={{ ml: 1, fontSize:'1rem', textAlign:'left' }}> ({productData.ratingCount} {productData.ratingCount === 1 && (<>review</> )} {productData.ratingCount != 1 && (<>reviews</> )})</Typography>
//                     </a>
//                   <Divider sx={{margin:'auto', mt:.5, mb:.5, mr:.5, backgroundColor:style.color2, width:'95%'}} />
//                   <Typography sx={{ ml: 0.5, fontSize:'1rem', fontWeight:'bold', textAlign:'left' }}> {productData.cardSummary}</Typography>
//                   <Typography sx={{ ml: 0.5, mb:1, mt:0.5, fontSize:'1rem', textAlign:'left' }}> <strong>VST3 Plugin</strong> - available for both Windows and Mac</Typography>
//                   {productData.price > 0 ? (
//                     <>
//                     <Typography
//                         variant="body2"
//                         sx={{ color: style.color3, ml:3,textDecoration: "line-through", fontSize: "1.1rem", textAlign:'left' }}
//                       >
//                         ${productData.msrp.toFixed(2)}
//                       </Typography>
//                       <Typography variant="h6" sx={{ml:3, mb:2, color: style.color4, fontSize:'2.2rem', textAlign:'left' }}>
//                         <strong>${(productData.price).toFixed(2)}</strong>
//                       </Typography>
                      
//                       <Button
//                         onClick={addProductToCart}
//                         sx={{
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                           border: `1px solid ${style.color2}`,
//                           width:'95%',
//                           margin:'auto',
//                           filter:'brightness(1.05)',
//                           boxShadow:10,
//                           fontWeight:'bold',
//                           fontSize:'.8rem',
//                           "&:hover": { filter:'brightness(1.2)'},
//                         }}
//                       >
//                         <img src={shoppingCartIcon} alt='Shopping cart icon' style={{ maxWidth:'20px', marginRight:'5px'}} />
//                         Add to Cart
//                       </Button>
//                       <Button
//                         href={'/products/' + productData.productURI}
//                         sx={{
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                           border: `1px solid ${style.color2}`,
//                           width:'95%',
//                           margin:'auto',
//                           filter:'brightness(1.05)',
//                           boxShadow:10,
//                           mt:2,
//                           mb:2,
//                           fontWeight:'bold',
//                           fontSize:'.8rem',
//                           "&:hover": { filter:'brightness(1.2)'},
//                         }}
//                       >
//                         <KeyboardDoubleArrowRightRoundedIcon sx={{ fontSize:'1.5rem', marginRight:'5px'}}/>
//                         Learn More
//                       </Button>
//                     </>
//                   ) : (
//                     <>
//                         {/* Free Product */}
//                         <div style={{margin:'auto'}}>
//                     <Box sx={{display:'flex', flexDirection:'row'}}>
//                     <Typography sx={{ml:0.5, color:'rgb(0,255,0)', fontSize:'1.5rem', fontWeight:'bold', textAlign:'left' }}> FREE </Typography>
//                     </Box>
//                     <Typography sx={{ml:.5, color:style.color4, fontSize:'1rem', textAlign:'left', fontWeight:'bold'}}>Download VST3 Plugin:</Typography>
//                     <div style={{ textAlign:'left', display:'flex', flexDirection:'row' }}>
//                     <Button onClick={downloadWindowsVST} sx={{ m:1, mt:0, minHeight:'60px', maxWidth: '175px', boxShadow:5, backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
                    
//                     <div style={{flex:1, minWidth:'60px' }}>
//                         <div style={{flex:1, padding:'10px', paddingBottom:'5px', backgroundColor:'black'}}>
//                             <img src='/windows_logo.png' style={{width:'100%', margin:'auto'}} />
//                         </div>
//                         <div style={{textAlign:'center', color:style.color4}}>
//                         <strong>Windows</strong>
//                             </div>
//                             </div>
//                     </Button>
//                     <Button onClick={downloadMacVST} sx={{ m:1, mt:0, boxShadow:5, minHeight:'60px', maxWidth:"175px", backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
                    
//                     <div style={{flex:1, minWidth:'60px'}}>
//                         <div style={{flex:1, padding:'10px', paddingBottom:'5px', backgroundColor:style.color4}}>
//                             <img src='/apple_logo.svg' style={{width:'95%', margin:'auto'}} />
//                         </div>
//                         <div style={{textAlign:'center', color:style.color4}}>
//                         <strong>Mac</strong>
//                             </div>
//                             </div>
//                     </Button>
//                     </div>
//                 </div>

//                     </>
//                   )}

//               </div> 
              
//               </Box>
//   </Box>
//   )}
  
//   <LoginModal isOpen={isLoginRegisterModalOpen} close={closeModal(setLoginRegisterModalOpen)} loginComplete={loginCallback}/>
//   </>)}